import { createApi } from '@reduxjs/toolkit/query/react';

import { getData, postData } from '../../services/API';
import {
  addEmployeeNoteUrl,
  employeeNotesUrl,
  employeeSearchUrl,
  employeeShiftStatusUrl,
  removeEmployeeNoteUrl
} from '../../services/API/endpoints';
import { processError, processResponse } from './utils/transformations';

interface IAddEmployeeNotePayload {
  userId: string;
  employeeId: string;
  author: string;
  message: string;
}

interface IRemoveEmployeeNotePayload {
  userId: string;
  employeeId: string;
  noteId: string;
}

interface EmpShiftStatusDto {
  coid: string;
  id: string;
  onShift: true;
  orbitKey: string;
  partitionKey: string;
  shiftStartTimeUtc: string;
  shiftEndTimeUtc: string;
  type: string;
  ttl: number;
  userId: string;
}

/**
 * RTK Query API slice for employee features.
 *
 * @remarks
 * Provides endpoints for:
 * - Employee details (`getEmployeeToRound`, etc.)
 * - Employee management endpoints as defined
 *
 * @example
 * import { useGetEmployeeToRoundQuery } from 'src/redux/apis/Employees.api';
 * const { data } = useGetEmployeeToRoundQuery(employeeId);
 */
export const employeesApi = createApi({
  reducerPath: 'employeesApi',
  baseQuery: (operation: any) => operation,
  tagTypes: [
    'employee-to-round',
    'employee-by-id',
    'employees-by-ids',
    'employees-by-search',
    'employee-notes',
    'shift-status-list'
  ],
  invalidationBehavior: 'immediately',
  endpoints: builder => ({
    postAddEmployeeNote: builder.mutation({
      query: (payload: IAddEmployeeNotePayload) =>
        postData(addEmployeeNoteUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, { employeeId }) => [
        {
          type: 'employee-notes',
          id: employeeId
        }
      ]
    }),
    postRemoveEmployeeNote: builder.mutation({
      query: (payload: IRemoveEmployeeNotePayload) =>
        postData(removeEmployeeNoteUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, { employeeId }) => [
        {
          type: 'employee-notes',
          id: employeeId
        }
      ]
    }),
    getEmployeeToRound: builder.query({
      query: (employeeId: string) =>
        getData(`${employeeSearchUrl}?employeeId=${employeeId}`, {}),
      transformResponse: response => response[0],
      transformErrorResponse: processError,
      providesTags: (_result, _error, employeeId) => [
        {
          type: 'employee-to-round',
          id: employeeId
        }
      ]
    }),
    getEmployeeById: builder.query({
      query: (id: string) =>
        getData(`${employeeSearchUrl}?employeeId=${id}`, {}),
      transformResponse: response => response[0],
      transformErrorResponse: processError,
      providesTags: (_result, _error, id) => [
        {
          type: 'employee-by-id',
          id
        }
      ]
    }),
    getEmployeesByIds: builder.query({
      query: async (ids: string[]) => {
        try {
          const responses = await Promise.all(
            ids.map(id => getData(`${employeeSearchUrl}?employeeId=${id}`, {}))
          );
          return { data: responses };
        } catch (error) {
          return { error };
        }
      },
      transformResponse: response =>
        response.map((res: any) => res.data[0]).filter((data: any) => data),
      transformErrorResponse: processError,
      providesTags: (_result, _error, ids) => [
        {
          type: 'employees-by-ids',
          id: ids.join(',')
        }
      ]
    }),
    getEmployeesBySearch: builder.query({
      query: (query: string) =>
        query.length > 1
          ? query.match(/\d+/g)
            ? getData(`${employeeSearchUrl}?employeeId=${query}`, {})
            : getData(`${employeeSearchUrl}?name=${query}`, {})
          : { data: [] },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, query) => [
        {
          type: 'employees-by-search',
          id: query
        }
      ]
    }),
    getEmployeeNotes: builder.query({
      query: (employeeId: string) =>
        getData(`${employeeNotesUrl}?employeeId=${employeeId}`, {}),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, employeeId) => [
        {
          type: 'employee-notes',
          id: employeeId
        }
      ]
    }),
    postShiftStatusListByIds: builder.query<EmpShiftStatusDto[], string[]>({
      query: (hcaIds: string[]) => postData(employeeShiftStatusUrl, hcaIds),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, hcaIds) => [
        {
          type: 'shift-status-list',
          id: hcaIds.join(',')
        }
      ]
    })
  })
});

export const {
  usePostAddEmployeeNoteMutation,
  usePostRemoveEmployeeNoteMutation,
  useGetEmployeeToRoundQuery,
  useLazyGetEmployeeToRoundQuery,
  useGetEmployeeByIdQuery,
  useLazyGetEmployeeByIdQuery,
  useGetEmployeesByIdsQuery,
  useLazyGetEmployeesByIdsQuery,
  useGetEmployeesBySearchQuery,
  useLazyGetEmployeesBySearchQuery,
  useGetEmployeeNotesQuery,
  useLazyGetEmployeeNotesQuery,
  usePostShiftStatusListByIdsQuery,
  useLazyPostShiftStatusListByIdsQuery
} = employeesApi;
